import {
  Card,
  Stack,
  Title,
  Text,
  Button,
  Box,
  Group,
  SimpleGrid,
  Paper,
  Switch,
  Divider,
} from '@mantine/core';
import GameScreen from '../components/GameScreen';

function PlayerLobbyScreen({
  gameName,
  roomCode,
  playerName,
  playerScore,
  players,
  isGameController,
  onStartGame,
  gameOptions,
  onGameOptionChange,
  onQuit,
}) {
  return (
    <GameScreen
      gameName={gameName}
      roomCode={roomCode}
      onQuit={onQuit}
      background="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      scroll
    >
      <Stack gap="lg" w="100%">
        {/* Start Game / Waiting */}
        <Card
          shadow="lg"
          p="lg"
          radius="lg"
          withBorder
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
          }}
        >
          {isGameController ? (
            <Stack gap="md" align="center">
              <Text size="xl" ta="center" c="blue" fw={600}>
                🎮 You're the Host!
              </Text>

              {/* Game Options */}
              <Box w="100%">
                <Text size="md" fw={500} mb="sm" ta="center" c="gray.7">
                  ⚙️ Game Settings
                </Text>
                <Stack gap="sm">
                  <Switch
                    label="Disable Timers"
                    description="No timers for a more relaxed game"
                    size="md"
                    checked={gameOptions?.disableTimers || false}
                    onChange={(event) =>
                      onGameOptionChange?.('disableTimers', event.currentTarget.checked)
                    }
                  />

                  <Switch
                    label="Extended Rounds"
                    description="Play 5 rounds instead of 3"
                    size="md"
                    checked={gameOptions?.extendedRounds || false}
                    onChange={(event) =>
                      onGameOptionChange?.('extendedRounds', event.currentTarget.checked)
                    }
                  />

                  <Switch
                    label="Simplified Drawing Tools"
                    description="Simple tools to speed up game play"
                    size="md"
                    checked={gameOptions?.simplifiedDrawing || false}
                    onChange={(event) =>
                      onGameOptionChange?.('simplifiedDrawing', event.currentTarget.checked)
                    }
                  />
                </Stack>
                <Divider my="md" color="rgba(255, 255, 255, 0.2)" />
              </Box>

              <Button
                size="lg"
                fullWidth
                onClick={onStartGame}
                disabled={players.length < 2}
                sx={{ fontSize: '1.3rem' }}
              >
                🚀 Start Game
              </Button>
              {players.length < 2 && (
                <Text size="sm" c="dimmed" ta="center">
                  Need at least 2 players to start
                </Text>
              )}
            </Stack>
          ) : (
            <Box ta="center">
              <Text size="lg" c="yellow" fw={600} mb="sm">
                ⏳ Waiting for game to start...
              </Text>
              <Text size="sm" c="dimmed">
                The Host will start the game when ready
              </Text>
            </Box>
          )}
        </Card>

        {/* Players List */}
        <Card
          shadow="lg"
          p="lg"
          radius="lg"
          withBorder
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            flex: 1,
          }}
        >
          <Stack gap="md">
            <Group justify="center" gap="sm">
              <Text size="2rem">👥</Text>
              <Title order={3} size="lg">
                Players ({players.length})
              </Title>
            </Group>

            <SimpleGrid cols={1} spacing="xs">
              {players.map((player) => (
                <Paper
                  key={player.id}
                  p="md"
                  radius="md"
                  sx={{
                    backgroundColor: player.isGameController
                      ? 'rgba(33, 150, 243, 0.2)'
                      : 'rgba(255, 255, 255, 0.1)',
                    border: `1px solid ${
                      player.isGameController ? '#2196F3' : 'rgba(255, 255, 255, 0.2)'
                    }`,
                  }}
                >
                  <Group justify="apart" align="center">
                    <Group gap="sm">
                      {player.avatar ? (
                        <img
                          src={player.avatar}
                          alt={player.name}
                          style={{
                            width: '2.5rem',
                            height: '2.5rem',
                            borderRadius: '50%',
                            border: '2px solid #e9ecef',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <Box
                          style={{
                            width: '2.5rem',
                            height: '2.5rem',
                            borderRadius: '50%',
                            backgroundColor: '#e9ecef',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.2rem',
                          }}
                        >
                          {player.isGameController ? '🎮' : '🎭'}
                        </Box>
                      )}
                      <Box>
                        <Text fw={600}>
                          {player.name}
                          {player.name === playerName && ' (You)'}
                        </Text>
                        {player.isGameController && (
                          <Text size="xs" c="blue">
                            Host
                          </Text>
                        )}
                      </Box>
                    </Group>
                  </Group>
                </Paper>
              ))}
            </SimpleGrid>
          </Stack>
        </Card>
      </Stack>
    </GameScreen>
  );
}

export default PlayerLobbyScreen;
